<script>
import moment from "moment";
import {mapGetters} from "vuex";
import {toGreekUpperCase} from "@utils/util";
import {round} from "@utils/numbers";
import {apiStatus} from "@utils/apiStatus";
import {
  microNutrientFields,
  bothCategoriesMicronutrients,
  trixopoulouMicronutrients,
  usdaMicronutrients
} from "@utils/nutrients";

const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

// Memoize i18n global usages to avoid recalculating constantly across all consumers.
const localeCache = new Map();
const perLocale = (locale, key, build) => {
  const cacheKey = `${locale}:${key}`;
  if (!localeCache.has(cacheKey)) localeCache.set(cacheKey, build());
  return localeCache.get(cacheKey);
};

export default {

  computed: {
    // Aliased to `isEducational` to keep the existing consumer name stable;
    // the canonical Vuex getter is `auth/isStudent`.
    ...mapGetters({
      isEducational: 'auth/isStudent',
    }),

    microNutrientFields() {
      return microNutrientFields;
    },
    bothCategoriesMicronutrients() {
      return bothCategoriesMicronutrients;
    },
    trixopoulouMicronutrients() {
      return trixopoulouMicronutrients;
    },
    usdaMicronutrients() {
      return usdaMicronutrients;
    },
    microNutrientTranslation() {
      return perLocale(this.$i18n.locale, 'microNutrientTranslation', () =>
        microNutrientFields.reduce((acc, val) => {
          acc[val] = this.$t(`dietPlans.microTranslation.${val}`);
          return acc;
        }, {})
      );
    },

    dayNames() {
      return perLocale(this.$i18n.locale, 'dayNames', () => [
        this.$t('days.monday'),
        this.$t('days.tuesday'),
        this.$t('days.wednesday'),
        this.$t('days.thursday'),
        this.$t('days.friday'),
        this.$t('days.saturday'),
        this.$t('days.sunday')
      ]);
    },
    dayNamesShort() {
      return perLocale(this.$i18n.locale, 'dayNamesShort', () => [
        this.$t('days.short.monday'),
        this.$t('days.short.tuesday'),
        this.$t('days.short.wednesday'),
        this.$t('days.short.thursday'),
        this.$t('days.short.friday'),
        this.$t('days.short.saturday'),
        this.$t('days.short.sunday')
      ]);
    },
    dayNamesAlt() {
      return perLocale(this.$i18n.locale, 'dayNamesAlt', () =>
        [1, 2, 3, 4, 5, 6, 7].map(x => this.$t('nthChoice', {num: x}))
      );
    },
    dayMeals() {
      return perLocale(this.$i18n.locale, 'dayMeals', () => [
        this.$t('meals.breakfast'),
        this.$t('meals.midBreakfast'),
        this.$t('meals.lunch'),
        this.$t('meals.afterLunch'),
        this.$t('meals.dinner'),
        this.$t('diet_plan.meals.5'),
        this.$t('diet_plan.meals.6'),
      ]);
    },
    elDatePicker() {
      return perLocale(this.$i18n.locale, 'elDatePicker', () => ({
        days: [
          this.$t('days.short.sunday'),
          this.$t('days.short.monday'),
          this.$t('days.short.tuesday'),
          this.$t('days.short.wednesday'),
          this.$t('days.short.thursday'),
          this.$t('days.short.friday'),
          this.$t('days.short.saturday')
        ].map(x => x.replace('.', '')), // replace `.` to make it look beter
        months: months.map(x => this.$t('months.short.' + x)),
        pickers: [
          this.$t('datePickerPlaceholders.nextSeven'),
          this.$t('datePickerPlaceholders.nextThirty'),
          this.$t('datePickerPlaceholders.prevSeven'),
          this.$t('datePickerPlaceholders.prevThirty'),
        ],
        placeholder: {
          date: this.$t('datePickerPlaceholders.date'),
          dateRange: this.$t('datePickerPlaceholders.range'),
        }
      }));
    },
    quillOptions() {
      return perLocale(this.$i18n.locale, 'quillOptions', () => ({
        placeholder: this.$t('insertNotes'),
        modules: {
          toolbar: {
            container: [["bold", "italic"],
              [{'list': 'ordered'}, {'list': 'bullet'}],
              [{'header': [1, 2, 3, 4, 5, 6, false]}],
              [{'color': []}, {'background': []}],
              ['link']
            ],
          },
        }
      }));
    },
  },
  data() {
    return {
      isOfflineVersion: process.env.VUE_APP_IS_LOCAL_INSTALLATION,
    }
  },

  methods: {
    // Template-side delegate for the canonical rounding helper (numbers.js):
    // safe on null/undefined, returns a trimmed Number.
    round,

    upper(str) {
      return toGreekUpperCase(str || '');
    },
    replaceCommaWithDot: value => {
      return value.replace(",", ".");
    },

    formatDate: value => {
      return moment(value).format('DD/MM/YYYY HH:mm');
    },

    formatDateWithoutHour: value => {
      return moment(value).format('DD/MM/YYYY');
    },

    formatDateWithoutHourAsLink: value => {
      return moment(value).format('DD-MM-YYYY');
    },

    // Single named entry point for the active-athletes modal — callers must
    // not inline the commit string (easy to typo, invisible to grep).
    triggerActiveAthletesModal(show = true) {
      this.$store.commit('athlete/SET_SHOW_ACTIVE_ATHLETES', show);
    },

    // Named delegate to the food store — the implementation lives in
    // food.js (ensureFoods); callers use this instead of inlining the dispatch string.
    fetchFoods() {
      return this.$store.dispatch('food/ensureFoods');
    },


    emitErrorNotification(props = {}) {
      if (!apiStatus.allowErrorToast()) return;
      this.$notify({
        group: 'athlisis-notifications',
        type: 'error',
        title: props.title || this.$t('errors.title'),
        text: props.text || this.$t('errors.text'),
        ...(props.duration ? { duration: props.duration } : {})
      });
    },

    emitSuccessNotification(props = {}) {
      this.$notify({
        group: 'athlisis-notifications',
        type: 'success',
        title: props.title || this.$t('success.title'),
        text: props.text || this.$t('success.text'),
        ...(props.duration ? { duration: props.duration } : {})
      });
    },

    // The app-wide delete-confirmation idiom (verifyDeletion.* texts, html
    // body, loader mode, backdrop closes). onOk receives the dialog handle —
    // call dialog.close() when the async work finishes. Cancel and backdrop
    // dismissals resolve silently.
    confirmDelete(onOk, texts = {}) {
      return this.$dialog.confirm({
        title: texts.title || this.$t('verifyDeletion.title'),
        body: texts.body || this.$t('verifyDeletion.message')
      }, {
        html: true,
        loader: true,
        okText: this.$t('verifyDeletion.ok'),
        cancelText: this.$t('verifyDeletion.cancel'),
        backdropClose: true
      }).then(onOk).catch(() => {});
    },
  }
}
</script>
